/**
 * TenantOrganicBackground
 *
 * Mismo patrón EXACTO que `PortalPreview` (editor) y `BrandedBackground`
 * (Cotizador). Garantiza que lo que el broker ve en su portal coincide con
 * el preview que la admin configura — base + linear (135deg) + 4-corner
 * radial mesh, idéntico opacities.
 *
 * Lee `--color-tenant-bg-1/2` que `PortalThemeContext` setea en `<html>`
 * desde `Company.portal_theme.organicBackground`. Cae a defaults indigo/
 * fuchsia si el tenant no configuró nada.
 */
import { memo } from 'react';

interface TenantOrganicBackgroundProps {
  className?: string;
}

const FALLBACK_COLOR_1 = '#e0e7ff';
const FALLBACK_COLOR_2 = '#fae8ff';
const C1 = `var(--color-tenant-bg-1, ${FALLBACK_COLOR_1})`;
const C2 = `var(--color-tenant-bg-2, ${FALLBACK_COLOR_2})`;

const FIXED_BASE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100svh',
  pointerEvents: 'none',
};

export const TenantOrganicBackground = memo(({ className = '' }: TenantOrganicBackgroundProps) => {
  return (
    <>
      {/* LIGHT — base + linear (0.25) + 4-corner radial mesh (0.15) */}
      <div
        className={`-z-20 dark:opacity-0 transition-opacity duration-500 ${className}`}
        style={{ ...FIXED_BASE, backgroundColor: '#f5f5f5' }}
      />
      <div
        className="-z-20 dark:opacity-0 transition-opacity duration-500"
        style={{
          ...FIXED_BASE,
          background: `linear-gradient(135deg, ${C1}, ${C2})`,
          opacity: 0.25,
        }}
      />
      <div
        className="-z-20 dark:opacity-0 transition-opacity duration-500"
        style={{
          ...FIXED_BASE,
          backgroundImage: `
            radial-gradient(at 0% 0%, ${C1} 0px, transparent 50%),
            radial-gradient(at 100% 0%, ${C2} 0px, transparent 50%),
            radial-gradient(at 100% 100%, ${C1} 0px, transparent 50%),
            radial-gradient(at 0% 100%, ${C2} 0px, transparent 50%)
          `,
          opacity: 0.15,
        }}
      />

      {/* DARK — base + linear (0.4) + 4-corner radial mesh (0.3) */}
      <div
        className="-z-20 opacity-0 dark:opacity-100 transition-opacity duration-500"
        style={{ ...FIXED_BASE, backgroundColor: '#1a1a1a' }}
      />
      <div
        className="-z-20 opacity-0 dark:opacity-100 transition-opacity duration-500"
        style={{
          ...FIXED_BASE,
          background: `linear-gradient(135deg, ${C1}, ${C2})`,
          opacity: 0.4,
        }}
      />
      <div
        className="-z-20 opacity-0 dark:opacity-100 transition-opacity duration-500"
        style={{
          ...FIXED_BASE,
          backgroundImage: `
            radial-gradient(at 0% 0%, ${C1} 0px, transparent 50%),
            radial-gradient(at 100% 0%, ${C2} 0px, transparent 50%),
            radial-gradient(at 100% 100%, ${C1} 0px, transparent 50%),
            radial-gradient(at 0% 100%, ${C2} 0px, transparent 50%)
          `,
          opacity: 0.3,
        }}
      />
    </>
  );
});
TenantOrganicBackground.displayName = 'TenantOrganicBackground';
